import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { useProcessApiClient } from '@/api/clients/useProcessApiClient.util.ts';
import {
  ContactItemDto,
  ProcessListItemDto,
  ProcessStatusDto,
  ResourceTypeDto,
  TaskItemDto,
} from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { OPEN_TASK_STATUSES } from '@/components/ui/statusPill/statusTone.util.ts';

export const LIST_DETAILS_STALE_TIME = 30_000;

export const LIST_DETAILS_PREVIEW_SIZE = 5;

const OPEN_TASKS_FETCH_SIZE = 20;

type PreviewPage<ITEM> = {
  items: ITEM[];
  total: number;
};

type PreviewResult<ITEM> = {
  isError: boolean;
  isLoading: boolean;
  items: ITEM[];
  total: number;
};

const toPreviewResult = <ITEM>(query: {
  data?: PreviewPage<ITEM>;
  isError: boolean;
  isPending: boolean;
}): PreviewResult<ITEM> => ({
  isError: query.isError,
  isLoading: query.isPending,
  items: query.data?.items ?? [],
  total: query.data?.total ?? 0,
});

const byDueDate = (left: TaskItemDto, right: TaskItemDto) =>
  dayjs(left.dueDate).valueOf() - dayjs(right.dueDate).valueOf();

export const useOpenResourceTasks = (params: {
  resourceId: string;
  resourceTypes: ResourceTypeDto[];
}): PreviewResult<TaskItemDto> => {
  const { resourceId, resourceTypes } = params;
  const { tasksApi } = useApiClient();

  const query = useQuery<PreviewPage<TaskItemDto>, AxiosError>({
    meta: { disableErrorAlert: true },
    queryFn: async () => {
      const { data } = await tasksApi.searchTasks(0, OPEN_TASKS_FETCH_SIZE, {
        contextResourceId: resourceId,
        contextResourceTypes: resourceTypes,
        taskStatuses: OPEN_TASK_STATUSES,
      });
      return { items: [...data.items].sort(byDueDate), total: data.totalResults };
    },
    queryKey: [QueryKeyEnum.LIST_DETAILS.OPEN_TASKS, resourceId, resourceTypes],
    staleTime: LIST_DETAILS_STALE_TIME,
  });

  return toPreviewResult(query);
};

export const useActiveProcesses = (params: {
  contactId?: number;
  customerId?: number;
}): PreviewResult<ProcessListItemDto> => {
  const { contactId, customerId } = params;
  const { processesApi } = useProcessApiClient();

  const query = useQuery<PreviewPage<ProcessListItemDto>, AxiosError>({
    meta: { disableErrorAlert: true },
    queryFn: async () => {
      const { data } = await processesApi.searchProcesses(0, LIST_DETAILS_PREVIEW_SIZE, {
        customerId,
        processContactId: contactId,
        statuses: [ProcessStatusDto.Created, ProcessStatusDto.InProgress],
      });
      return { items: data.items, total: data.totalResults };
    },
    queryKey: [QueryKeyEnum.LIST_DETAILS.ACTIVE_PROCESSES, customerId, contactId],
    staleTime: LIST_DETAILS_STALE_TIME,
  });

  return toPreviewResult(query);
};

export const useCustomerContactsPreview = (customerId: number): PreviewResult<ContactItemDto> => {
  const { customersApi } = useApiClient();

  const query = useQuery<PreviewPage<ContactItemDto>, AxiosError>({
    meta: { disableErrorAlert: true },
    queryFn: async () => {
      const { data } = await customersApi.searchCustomerContacts(
        0,
        LIST_DETAILS_PREVIEW_SIZE,
        customerId,
        { fullTextSearchInput: '' },
      );
      return { items: data.items, total: data.totalResults };
    },
    queryKey: [QueryKeyEnum.LIST_DETAILS.CUSTOMER_CONTACTS, customerId],
    staleTime: LIST_DETAILS_STALE_TIME,
  });

  return toPreviewResult(query);
};

export const useSupplierContactsPreview = (supplierId: number): PreviewResult<ContactItemDto> => {
  const { suppliersApi } = useApiClient();

  const query = useQuery<PreviewPage<ContactItemDto>, AxiosError>({
    meta: { disableErrorAlert: true },
    queryFn: async () => {
      const { data } = await suppliersApi.searchSupplierContacts(
        0,
        LIST_DETAILS_PREVIEW_SIZE,
        supplierId,
        {},
      );
      return { items: data.items, total: data.totalResults };
    },
    queryKey: [QueryKeyEnum.LIST_DETAILS.SUPPLIER_CONTACTS, supplierId],
    staleTime: LIST_DETAILS_STALE_TIME,
  });

  return toPreviewResult(query);
};
